import React from 'react'
import { Bot, ChevronLeft, ChevronRight, EllipsisVertical, Kanban, PencilIcon, Plus, Trash } from 'lucide-react'
import { Dropdown } from '../../components/ui/dropdown/Dropdown'
import { DropdownItem } from '../../components/ui/dropdown/DropdownItem'
import { useBusiness } from '../../context/BusinessContext'
import { api } from '../../configs/api'
import toast from 'react-hot-toast'

interface BusinessOptionsProps {
    handleCreate: () => void
    handleEdit: () => void
    handleCreateStep: () => void
    handleDeletePipeline: () => void
}

interface PositionProps {
    position: 'unique' | 'first' | 'last' | 'middle'
    setPosition: (position: string) => void
}

const BusinessOptions = ({
    handleCreate,
    handleEdit,
    handleCreateStep,
    handleDeletePipeline
}: BusinessOptionsProps) => {
    const { business, currentTab, startLoading, stopLoading, setBusiness, setFilteredBusiness } = useBusiness()
    const [position, setPosition] = React.useState<PositionProps['position']>('middle')
    const [isOpen, setIsOpen] = React.useState(false)

    const toggleDropdown = () => {
        setIsOpen(!isOpen)
    }

    const closeDropdown = () => {
        setIsOpen(false)
    }

    const movePipeline = async (direction: 'left' | 'right') => {
        if (!business) return
        if (!business.pipelines) return
        if (!business.pipelines.length) return

        const pipelineID = business.pipelines.find((pipeline: any) => pipeline.value === currentTab)?.id
        if (!pipelineID) return

        try {
            startLoading()

            //* Atualiza BD
            const response = await fetch(`${api}business/movePipeline`, {
                method: 'POST',
                body: JSON.stringify({
                    pipelineID,
                    direction
                })
            })

            const result = await response.json()
            if (!response.ok || !result.success) {
                toast.error(result.message || 'Erro ao mover o pipeline!')
                return
            }

            //? Atualiza contexto
            const updatedBusiness = [...business.pipelines] // Clona o array de pipelines
            const pipelineIndex = updatedBusiness.findIndex(pipeline => pipeline.value === currentTab)

            if (
                pipelineIndex !== -1 &&
                ((direction === 'right' && pipelineIndex + 1 < updatedBusiness.length) ||
                    (direction === 'left' && pipelineIndex - 1 >= 0))
            ) {
                const targetIndex = direction === 'right' ? pipelineIndex + 1 : pipelineIndex - 1

                // Troca as posições das pipelines
                ;[updatedBusiness[pipelineIndex], updatedBusiness[targetIndex]] = [
                    updatedBusiness[targetIndex],
                    updatedBusiness[pipelineIndex]
                ]
            }

            setBusiness({ pipelines: updatedBusiness })
            setFilteredBusiness({ pipelines: updatedBusiness })

            toast.success('Pipeline movido com sucesso!')
        } catch (error) {
            console.log(error)
        } finally {
            stopLoading()
        }
    }

    React.useEffect(() => {
        //? Verifica se o pipeline/business atual é o primeiro ou o último pra bloquear opções de movimentação
        if (business) {
            const totalPipelines = business.pipelines.length

            if (totalPipelines === 1) {
                setPosition('unique')
            } else {
                const firstPipeline = business.pipelines[0]
                const lastPipeline = business.pipelines[totalPipelines - 1]

                if (firstPipeline.value === currentTab) {
                    setPosition('first')
                } else if (lastPipeline.value === currentTab) {
                    setPosition('last')
                } else {
                    setPosition('middle')
                }
            }
        }
    }, [isOpen])

    return (
        <div className='relative'>
            <div
                className='flex items-center justify-center cursor-pointer p-2 bg-gray-100 hover:bg-gray-200 dark:bg-white/[0.03] dark:hover:bg-white/[0.05] rounded-full'
                onClick={toggleDropdown}
            >
                <EllipsisVertical className='hidden md:block w-4 h-4' />
                <p className='block md:hidden text-sm'>Opções</p>
            </div>

            <Dropdown
                isOpen={isOpen}
                onClose={closeDropdown}
                className='absolute right-0 mt-[17px] flex w-[260px] flex-col rounded-2xl border border-gray-200 bg-white p-3 shadow-theme-lg dark:border-gray-800 dark:bg-gray-dark z-50'
            >
                <ul className='flex flex-col gap-1 pt-4 pb-3 '>
                    <li>
                        <DropdownItem
                            onItemClick={closeDropdown}
                            tag='a'
                            onClick={handleCreate}
                            className='flex items-center gap-3 px-3 py-2 font-medium text-gray-700 rounded-lg group text-theme-sm hover:bg-gray-100 hover:text-gray-700 dark:text-gray-400 dark:hover:bg-white/5 dark:hover:text-gray-300'
                        >
                            <Plus className='w-4 h-4' />
                            Novo pipeline
                        </DropdownItem>
                    </li>
                    <li>
                        <DropdownItem
                            onItemClick={closeDropdown}
                            onClick={handleEdit}
                            className='flex items-center gap-3 px-3 py-2 font-medium text-gray-700 rounded-lg group text-theme-sm hover:bg-gray-100 hover:text-gray-700 dark:text-gray-400 dark:hover:bg-white/5 dark:hover:text-gray-300'
                        >
                            <PencilIcon className='w-4 h-4' />
                            Alterar pipeline
                        </DropdownItem>
                    </li>
                    <li>
                        <DropdownItem
                            onItemClick={closeDropdown}
                            onClick={() => movePipeline('left')}
                            disabled={position === 'first' || position === 'unique'}
                            className='flex items-center gap-3 px-3 py-2 font-medium text-gray-700 rounded-lg group text-theme-sm hover:bg-gray-100 hover:text-gray-700 dark:text-gray-400 dark:hover:bg-white/5 dark:hover:text-gray-300'
                        >
                            <ChevronLeft className='w-4 h-4' />
                            Mover para esquerda
                        </DropdownItem>
                    </li>
                    <li>
                        <DropdownItem
                            onItemClick={closeDropdown}
                            onClick={() => movePipeline('right')}
                            disabled={position === 'last' || position === 'unique'}
                            className='flex items-center gap-3 px-3 py-2 font-medium text-gray-700 rounded-lg group text-theme-sm hover:bg-gray-100 hover:text-gray-700 dark:text-gray-400 dark:hover:bg-white/5 dark:hover:text-gray-300'
                        >
                            <ChevronRight className='w-4 h-4' />
                            Mover para direita
                        </DropdownItem>
                    </li>
                    <li>
                        <DropdownItem
                            onItemClick={closeDropdown}
                            tag='a'
                            onClick={handleCreateStep}
                            className='flex items-center gap-3 px-3 py-2 font-medium text-gray-700 rounded-lg group text-theme-sm hover:bg-gray-100 hover:text-gray-700 dark:text-gray-400 dark:hover:bg-white/5 dark:hover:text-gray-300'
                        >
                            <Kanban className='w-4 h-4' />
                            Nova fase
                        </DropdownItem>
                    </li>
                    <li>
                        <DropdownItem
                            onItemClick={closeDropdown}
                            onClick={handleDeletePipeline}
                            className='px-3 py-2 font-medium rounded-lg group text-theme-sm hover:bg-gray-100 hover:text-gray-700 dark:text-error-500 dark:hover:bg-white/5 dark:hover:text-error-300 light:text-error-500'
                        >
                            <div className='text-error-400 flex items-center gap-3'>
                                <Trash className='w-4 h-4' />
                                Excluir pipeline
                            </div>
                        </DropdownItem>
                    </li>
                </ul>
            </Dropdown>
        </div>
    )
}

export default BusinessOptions
